import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Gift, Copy, Share, Users, Ticket, WhatsappLogo } from "@phosphor-icons/react";

export default function ReferralPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referral/me").then(({ data }) => setData(data));
  }, []);

  if (!data) return (
    <div className="max-w-4xl mx-auto px-6 py-16 text-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin inline-block" />
    </div>
  );

  const shareUrl = `${window.location.origin}${data.share_url}`;
  const msg = `Namaste! I'm using Zyro for hotels and long-stay rentals — verified stays, secure payments. Sign up with my code ${data.referral_code} and we both get ₹500 off. ${shareUrl}`;
  const waLink = `https://wa.me/?text=${encodeURIComponent(msg)}`;

  const copyCode = () => { navigator.clipboard.writeText(data.referral_code); toast.success("Code copied"); };
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14" data-testid="referral-page">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Rewards</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-3">Invite &amp; Earn ₹500</h1>
      <p className="text-neutral-600 mb-10 max-w-2xl">Share your code. When a friend completes their first paid booking, <b>you both get ₹500 off</b> your next stay. No limits — invite as many as you like.</p>

      {/* Hero card */}
      <div className="bg-gradient-to-br from-midnight via-midnight to-black text-bone rounded-3xl p-8 shadow-xl mb-8">
        <div className="flex items-center gap-2 text-gold text-[10px] tracking-widest uppercase mb-3">
          <Gift size={14} weight="fill" /> Your unique code
        </div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="font-mono tracking-[0.3em] text-3xl md:text-4xl text-white" data-testid="referral-code">{data.referral_code}</p>
            <p className="text-xs text-white/60 mt-2 break-all">{shareUrl}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={copyCode} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-xs tracking-widest uppercase" data-testid="copy-ref-code">
              <Copy size={14} /> Copy code
            </button>
            <button onClick={copyLink} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full text-xs tracking-widest uppercase" data-testid="copy-ref-link">
              <Share size={14} /> Copy link
            </button>
            <a href={waLink} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-full text-xs tracking-widest uppercase" data-testid="share-whatsapp">
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-neutral-500 mb-2"><Users size={12} /> Friends invited</div>
          <p className="font-serif text-4xl text-midnight" data-testid="stat-referred">{data.referred_count}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-emerald-800 mb-2"><Gift size={12} weight="fill" /> Converted</div>
          <p className="font-serif text-4xl text-emerald-900" data-testid="stat-converted">{data.converted_count}</p>
        </div>
        <div className="bg-gold-soft border border-gold/20 rounded-2xl p-6">
          <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-gold mb-2"><Ticket size={12} weight="fill" /> Unredeemed credit</div>
          <p className="font-serif text-4xl text-midnight" data-testid="stat-credit">₹{data.unredeemed_credit.toLocaleString("en-IN")}</p>
        </div>
      </div>

      {/* Coupons earned */}
      {data.coupons && data.coupons.length > 0 && (
        <div className="bg-white border border-black/5 rounded-2xl p-6 mb-10" data-testid="referral-coupons">
          <h3 className="font-serif text-xl text-midnight mb-4">Your referral coupons</h3>
          <div className="space-y-2">
            {data.coupons.map((c) => (
              <div key={c.id} className="flex items-center justify-between border border-dashed border-gold/40 rounded-xl p-3">
                <div>
                  <p className="font-mono tracking-widest text-midnight">{c.code}</p>
                  <p className="text-xs text-neutral-500">₹{c.max_discount} off on bookings ≥ ₹{c.min_amount} · {(c.used_count || 0) > 0 ? "Used" : "Unused"}</p>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Coupon code copied"); }}
                        className="p-2 text-gold hover:text-midnight" data-testid={`copy-coupon-${c.code}`}>
                  <Copy size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* How it works */}
      <div className="bg-stone/40 rounded-2xl p-8">
        <h3 className="font-serif text-xl text-midnight mb-6">How it works</h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm text-neutral-700">
          {[
            { n: "1", t: "Share your code", d: "Send your unique code to friends via WhatsApp or copy the link." },
            { n: "2", t: "Friend books their first stay", d: "They sign up with your code and complete their first paid booking or rental." },
            { n: "3", t: "You both get ₹500 off", d: "A coupon lands in both your accounts, redeemable on your next booking above ₹1000." },
          ].map((s) => (
            <div key={s.n}>
              <div className="w-10 h-10 rounded-full bg-midnight text-bone font-serif text-lg flex items-center justify-center mb-3">{s.n}</div>
              <p className="font-serif text-lg text-midnight mb-1">{s.t}</p>
              <p className="text-xs text-neutral-500">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
